/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { useAccessibleDropdown, useUniqueId } from '@squiz/dxp-console-support';
import clsx from 'clsx';
import React, { Ref, forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { DXP_ACTIVE_CLASS } from '../../../../Constants/Design';
import { ICON_CHEVRON_LEFT } from '../../../../Icons';
import { SdsIcon } from '../../../SdsIcon';
import { FormField } from '../../FormField';
import { SelectFieldOption } from './SelectField.defs';
import { SelectFieldProps, selectPropsDefaults } from './SelectField.props';

/**
 * SDS Select Field component.
 */
export const SelectField = forwardRef((props: SelectFieldProps, ref: Ref<HTMLInputElement>): JSX.Element => {
  const {
    callback,
    classes,
    customChange,
    defaultOption,
    disabled,
    errorMessage,
    fieldLabel,
    hideLabel,
    id,
    name,
    options,
    placeholder,
    readOnly,
    requiresCustomPosition = false,
    value,
    valuePrefix,
    ...rest
  } = {
    ...selectPropsDefaults,
    ...props,
  };

  // Note: The label here is used for screen readers
  const emptyOption = useMemo(() => {
    return {
      key: placeholder,
      label: placeholder,
      value: ``,
    };
  }, [placeholder]);

  const uniqueId = useUniqueId();
  const wrapperId = `sds-select-field--${uniqueId}`;

  const optionCount = options.length;
  const { accessibleDropdownButtonProps, accessibleDropdownItemProps, dropdownOpen, setDropdownOpen } =
    useAccessibleDropdown({
      dropdownSelector: `#${wrapperId}`,
      itemCount: optionCount,
    });

  const [chosenOption, setOption] = useState<SelectFieldOption>(defaultOption || emptyOption);

  // TODO: PLATFORM-612 states
  const [isCustomPosition, setCustomPosition] = useState(requiresCustomPosition);
  const [dropdownWidth, setDropdownWidth] = useState<string>('');
  const [dropdownOffsetHeight, setDropdownOffsetHeight] = useState<string>('');

  const selectRef = useRef<HTMLDivElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);

  const placeholderClass = chosenOption.value === `` ? `placeholder` : ``;

  const selectFieldClass = clsx(`sds-select-field`, {
    [DXP_ACTIVE_CLASS]: !readOnly && dropdownOpen,
    [`sds-select-field--custom`]: !readOnly && isCustomPosition,
  });
  const selectFieldOptionsClass = clsx(`sds-select-field__options`, {
    [`sds-select-field__options--custom`]: isCustomPosition,
  });

  const onOptionClick = (option: SelectFieldOption): void => {
    setOption(option);
    setDropdownOpen(false);
    callback && callback(option);
  };

  useEffect(() => {
    // State initialisation only occurs on the first render, so put this here as a back up
    // for setting the default option.
    // Also allow 'value' to set the selected option.
    if (value !== undefined && chosenOption.value !== value) {
      const selectedOption = options.find((option) => option.value === value);
      setOption(selectedOption || defaultOption || emptyOption);
    }
  }, [chosenOption, defaultOption, emptyOption, options, value]);

  // TODO: PLATFORM-612
  const updateDropdownPosition = (): void => {
    const selectFieldDiv = selectRef.current;
    const optionsDiv = optionsRef.current;

    if (!selectFieldDiv || !optionsDiv) {
      return;
    }

    const boundRect = selectFieldDiv.getBoundingClientRect();
    if (boundRect) {
      const scrollHeight = boundRect.top + boundRect.height;
      setDropdownOffsetHeight(`${scrollHeight}px`);
    }
  };

  // TODO: PLATFORM-612
  const updateDropdownWidth = (): void => {
    const selectFieldDiv = selectRef.current;
    if (!selectFieldDiv) {
      return;
    }

    // Width needs to adapt to any visible scrollbars
    const boundRect = selectFieldDiv.getBoundingClientRect();
    const width = `${boundRect.width}px`;
    setDropdownWidth(width);
  };

  // TODO: PLATFORM-612 generalise the select dropdown solution inside modals AND tables
  // Determines if dropdown options should be displayed over the modal or not.
  useEffect(() => {
    if (!requiresCustomPosition) {
      return;
    }

    const selectFieldDiv = selectRef.current;
    const dialogBody = selectFieldDiv?.closest('.sds-dialog__body');
    const optionsDiv = optionsRef.current;

    if (!selectFieldDiv || !dialogBody || !optionsDiv) {
      return;
    }

    const dialogOffsetTop = dialogBody.getBoundingClientRect().top;
    const optionsOffsetTop = optionsDiv.getBoundingClientRect().top;

    // Keep custom positioning to allow dropdown to display over the dialog
    // if dropdown options are not overlapping with the dialog header.
    if (optionsOffsetTop && optionsOffsetTop > dialogOffsetTop) {
      setCustomPosition(true);
    } else {
      setCustomPosition(false);
    }
  }, [dropdownOffsetHeight, requiresCustomPosition]);

  // TODO: PLATFORM-612 generalise the select dropdown solution inside modals AND tables
  // Handles width and vertical positioning of dropdown options while scrolling and resizing
  useEffect(() => {
    if (!requiresCustomPosition) {
      return;
    }

    const selectFieldDiv = selectRef.current;
    const dialogBody = selectFieldDiv?.closest('.sds-dialog__body');

    if (!selectFieldDiv || !dialogBody) {
      return;
    }

    const handleScroll = (): void => updateDropdownPosition();

    const handleResize = (): void => {
      updateDropdownWidth();
      updateDropdownPosition();
    };

    dialogBody.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    return (): void => {
      dialogBody.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [requiresCustomPosition]);

  // TODO: PLATFORM-612 generalise the select dropdown solution inside modals AND tables
  // Handle positioning and width of dropdown on click of select field
  useEffect(() => {
    if (!requiresCustomPosition) {
      return;
    }

    const selectFieldDiv = selectRef.current;
    const dialogBody = selectFieldDiv?.closest('.sds-dialog__body');
    const optionsDiv = optionsRef.current;

    if (!selectFieldDiv || !dialogBody || !optionsDiv) {
      return;
    }

    // Width needs to adapt to any visible scrollbars
    updateDropdownWidth();

    // Vertical position needs to account for any scrolling
    updateDropdownPosition();
  }, [dropdownOpen, requiresCustomPosition]);

  const getOptions = (): Array<JSX.Element> => {
    return options.map((option, index): JSX.Element => {
      return (
        <li className="sds-select-option" data-id={option.dataId} key={option.key}>
          <button
            {...accessibleDropdownItemProps[index]}
            className="sds-select-option__button"
            data-value={option.value}
            onClick={(event): void => {
              event.preventDefault();
              onOptionClick(option);
            }}
          >
            {option.label}
          </button>
        </li>
      );
    });
  };

  // Configure field
  const formFieldProps = { ...props };
  formFieldProps.classes = clsx(`sds-form-input`, classes, {
    [`readonly`]: readOnly,
    [`sds-form-input--custom`]: isCustomPosition,
  });

  const fieldIcon = (
    <div className="sds-select-field__icon">
      <SdsIcon svg={ICON_CHEVRON_LEFT} />
    </div>
  );

  const fieldText = chosenOption.label && chosenOption.label.trim() ? chosenOption.label : <br />;

  if (readOnly) {
    return (
      <FormField {...formFieldProps}>
        <div className={selectFieldClass} id={wrapperId} ref={selectRef} {...rest}>
          <span className="sds-select-field__button readonly">{fieldText}</span>
          {fieldIcon}
        </div>
      </FormField>
    );
  }

  // Dynamic positioning for the dropdown options
  const selectFieldStyle = isCustomPosition ? { style: { top: dropdownOffsetHeight, width: dropdownWidth } } : {};
  const selectFieldOptionsProps = {
    className: selectFieldOptionsClass,
    ...selectFieldStyle,
  };

  return (
    <FormField {...formFieldProps}>
      <input id={id} name={name} onChange={customChange} ref={ref} type="hidden" value={value} />
      <div className={selectFieldClass} id={wrapperId} ref={selectRef} {...rest}>
        <button
          {...accessibleDropdownButtonProps}
          aria-label={chosenOption.value && `${chosenOption.label}, ${emptyOption.label}`}
          className={`sds-select-field__button ${placeholderClass}`}
          disabled={disabled}
          type="button"
        >
          <span>
            {valuePrefix}
            {fieldText}
          </span>
          {fieldIcon}
        </button>
        {dropdownOpen && (
          <div {...selectFieldOptionsProps} ref={optionsRef}>
            <ul className="sds-select-list">{getOptions()}</ul>
          </div>
        )}
      </div>
    </FormField>
  );
});
